'use client';

import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { getRouteCnName } from '@/utils/route-metadata';
import { buildProductTrackingProperties } from '@/utils/tracking/productTrackingProperties';
import { usePathname } from 'next/navigation';
import { useCallback } from 'react';

/**
 * 镜片专题页埋点追踪Hook
 * 封装页面浏览和营销位曝光两个埋点事件，供三个镜片专题页使用
 */
export function useTrack() {
  const { trackEvent } = useReporting();
  const pathname = usePathname();
  const { getFromRouterInfo } = useRouteInfo();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  /**
   * 神策埋点：镜片专题页页面浏览
   * 对应Vue版本中的 SDPageView 函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPage_View
   * @EventDesc 镜片专题页页面浏览
   * @EventParams {
   *   page_name: 页面名称,
   *   refer_page_name: 来源页面名称,
   *   refer_url_path: 来源页面路径,
   * }
   */
  const trackPageView = useCallback(() => {
    try {
      // 获取当前页面的中文名称
      const page_name = getRouteCnName(pathname) || '';
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      const eventParams = {
        page_name,
        refer_page_name,
        refer_url_path,
      };

      trackEvent(
        {
          eventName: 'KnowledgeTopicPage_View',
          properties: eventParams,
        },
        [ProviderType.SENSORS],
      );

      logger.log('🔍 神策埋点 - KnowledgeTopicPage_View:', eventParams);
    } catch (error) {
      logger.error('KnowledgeTopicPage_View tracking error:', error);
    }
  }, [trackEvent, pathname]);

  /**
   * 神策埋点：镜片专题页营销位曝光
   * 对应Vue版本中的 SDPageMkExp 函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageMkt_Exp
   * @EventDesc 镜片专题页营销位曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   mkt_name: 营销位名称,
   * }
   * @param mkt_name 营销位名称
   */
  const trackMktExp = useCallback(
    (mkt_name: string) => {
      try {
        // 获取当前页面的中文名称
        const page_name = getRouteCnName(pathname) || '';

        const eventParams = {
          page_name,
          mkt_name,
        };

        trackEvent(
          {
            eventName: 'KnowledgeTopicPageMkt_Exp',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('KnowledgeTopicPageMkt_Exp tracking error:', error);
      }
    },
    [trackEvent, pathname],
  );

  /**
   * 神策埋点：镜片专题页 Banner 曝光
   * 对应Vue版本中 Banner 组件的 SDPageMkExp 函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageBanner_Exp
   * @EventDesc 镜片专题页 Banner 曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   mkt_name: 营销位名称 (固定为 'banner'),
   *   group_id: 分组ID,
   * }
   * @param group_id 分组ID (可选)
   */
  const trackBannerExp = useCallback(
    (group_id?: string | number) => {
      try {
        // 获取当前页面的中文名称
        const page_name = getRouteCnName(pathname) || '';
        const eventParams = {
          page_name,
          mkt_name: 'banner',
          ...(group_id && { group_id: String(group_id) }),
        };

        trackEvent(
          {
            eventName: 'KnowledgeTopicPageBanner_Exp',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );

        logger.log('🔍 神策埋点 - KnowledgeTopicPageBanner_Exp:', eventParams);
      } catch (error) {
        logger.error('KnowledgeTopicPageBanner_Exp tracking error:', error);
      }
    },
    [trackEvent, pathname],
  );

  const getMktParams = useCallback(
    (params: any, index: number) => {
      return {
        page_name: getRouteCnName(pathname) || '',
        mkt_name: '商品位',
        pits_index: index,
        ...buildProductTrackingProperties(
          params,
          getPriceInfo(userInfo, params, currency),
          getPriceInfo(userInfo, params, { name: env.NEXT_PUBLIC_DEFAULT_CURRENCY }),
          currency,
        ),
      };
    },
    [currency, pathname, userInfo],
  );

  /**
   * 神策埋点：镜片专题页广告坑位曝光
   * 对应Vue版本中的 sdProductListPageADExp 函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageADPits_Exp
   * @EventDesc 镜片专题页广告坑位曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   mkt_name: 营销位名称 (固定为 '商品位'),
   *   pits_index: 坑位排序,
   *   pits_jump_type: 内容的跳转类型,
   *   pits_jump_content: 跳转的内容,
   * }
   * @param item 广告项数据
   * @param index 坑位索引
   */
  const trackAdPitsExp = useCallback(
    (item: any, index: number) => {
      try {
        const eventParams = {
          page_name: getRouteCnName(pathname) || '',
          mkt_name: '商品位',
          pits_index: index,
          pits_jump_type: `${item.pits_jump_info?.jump_type || ''}`,
          pits_jump_content: `${item.pits_jump_info?.Jump_target || ''}`,
        };

        trackEvent(
          {
            eventName: 'KnowledgeTopicPageADPits_Exp',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );

        logger.log('🔍 神策埋点 - KnowledgeTopicPageADPits_Exp:', eventParams);
      } catch (error) {
        logger.error('KnowledgeTopicPageADPits_Exp tracking error:', error);
      }
    },
    [trackEvent, pathname],
  );

  /**
   * 神策埋点：镜片专题页广告坑位点击
   * 对应Vue版本中的 sdProductListPageADClick 函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageADPits_Click
   * @EventDesc 镜片专题页广告坑位点击
   * @EventParams {
   *   page_name: 页面名称,
   *   mkt_name: 营销位名称 (固定为 '商品位'),
   *   pits_index: 坑位排序,
   *   pits_jump_type: 内容的跳转类型,
   *   pits_jump_content: 跳转的内容,
   * }
   * @param params 广告参数
   */
  const trackAdPitsClick = useCallback(
    (params: any) => {
      try {
        const eventParams = {
          page_name: getRouteCnName(pathname) || '',
          mkt_name: '商品位',
          pits_index: params.pits_index,
          pits_jump_type: params.pits_jump_type,
          pits_jump_content: params.pits_jump_content,
        };

        trackEvent(
          {
            eventName: 'KnowledgeTopicPageADPits_Click',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );

        logger.log('🔍 神策埋点 - KnowledgeTopicPageADPits_Click:', eventParams);
      } catch (error) {
        logger.error('KnowledgeTopicPageADPits_Click tracking error:', error);
      }
    },
    [trackEvent, pathname],
  );

  /**
   * 神策埋点：镜片专题页商品坑位曝光
   * 对应Vue版本中的 sdProductListPageMktExp 函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageProductPits_Exp
   * @EventDesc 镜片专题页商品坑位曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   mkt_name: 营销位名称 (固定为 '商品位'),
   *   pits_index: 坑位排序,
   *   product_id: 商品ID,
   *   shop_sku: 商店SKU,
   *   business_sku: 业务SKU,
   *   product_sku: 产品SKU,
   * }
   * @param params 商品参数
   */
  const trackProductPitsExp = useCallback(
    (index: number, params: any) => {
      try {
        // 如果是广告类型，调用广告曝光埋点
        if (params.data_type === 'adv') {
          trackAdPitsExp(params, params.product_index);
          return;
        }

        const eventParams = getMktParams(params, index);

        trackEvent(
          {
            eventName: 'KnowledgeTopicPageProductPits_Exp',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );

        logger.log('🔍 神策埋点 - KnowledgeTopicPageProductPits_Exp:', eventParams);
      } catch (error) {
        logger.error('KnowledgeTopicPageProductPits_Exp tracking error:', error);
      }
    },
    [trackEvent, getMktParams, trackAdPitsExp],
  );

  /**
   * 神策埋点：镜片专题页商品坑位点击
   * 对应Vue版本中的 SDProductListPageMkt_Click 函数
   * @TrackType sensorsdata
   * @EventType KnowledgeTopicPageProductPits_Click
   * @EventDesc 镜片专题页商品坑位点击
   * @EventParams {
   *   page_name: 页面名称,
   *   mkt_name: 营销位名称 (固定为 '商品位'),
   *   pits_index: 坑位排序,
   *   product_id: 商品ID,
   *   shop_sku: 商店SKU,
   *   business_sku: 业务SKU,
   *   product_sku: 产品SKU,
   * }
   * @param params 商品参数
   */
  const trackProductPitsClick = useCallback(
    (index: number, params: any) => {
      try {
        const eventParams = getMktParams(params, index);

        trackEvent(
          {
            eventName: 'KnowledgeTopicPageProductPits_Click',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );

        logger.log('🔍 神策埋点 - KnowledgeTopicPageProductPits_Click:', eventParams);
      } catch (error) {
        logger.error('KnowledgeTopicPageProductPits_Click tracking error:', error);
      }
    },
    [trackEvent, getMktParams],
  );

  return {
    /**
     * 页面浏览埋点
     * 建议在页面加载完成时调用
     */
    trackPageView,

    /**
     * 营销位曝光埋点
     * @param mkt_name 营销位名称
     */
    trackMktExp,

    /**
     * Banner 曝光埋点
     * @param group_id 分组ID (可选)
     */
    trackBannerExp,

    /**
     * 商品坑位曝光埋点
     * @param params 商品参数
     */
    trackProductPitsExp,

    /**
     * 商品坑位点击埋点
     * @param params 商品参数
     */
    trackProductPitsClick,

    /**
     * 广告坑位曝光埋点
     * @param item 广告项数据
     * @param index 坑位索引
     */
    trackAdPitsExp,

    /**
     * 广告坑位点击埋点
     * @param params 广告参数
     */
    trackAdPitsClick,

    /**
     * 获取商品营销位参数的辅助函数
     * @param params 商品参数
     */
    getMktParams,
  };
}
